import { useState } from "react";
import { Alert, Checkbox, Modal } from "antd";

import { ROLE_OPTIONS, type AssumedRole } from "@/contexts/AssumedRoleContext";

import { ALL_ROLES } from "@/views/DocumentWorkspace/documentWorkspaceData";
import type { StudyDocumentRow } from "@/views/DocumentWorkspace/documentWorkspace.types";

/**
 * Change which roles may access a document.
 *
 * Widening access is the consequential direction: it can expose information and nothing
 * un-exposes it, so a widening change says so before it is saved. Narrowing is reversible
 * in effect, if not in history.
 */
export function RoleAccessModal({
  document,
  onCancel,
  onSave,
}: {
  document: StudyDocumentRow | null;
  onCancel: () => void;
  onSave: (roles: AssumedRole[]) => void;
}) {
  const [selected, setSelected] = useState<AssumedRole[]>(
    document?.role_access ?? [],
  );
  // Reset when a different document is opened, adjusted during render rather than in an
  // effect so the first paint already shows that document's roles.
  const [openedId, setOpenedId] = useState(document?.id ?? null);
  if (document && document.id !== openedId) {
    setOpenedId(document.id);
    setSelected(document.role_access);
  }

  const added = document
    ? selected.filter((role) => !document.role_access.includes(role))
    : [];
  const removed = document
    ? document.role_access.filter((role) => !selected.includes(role))
    : [];

  const labelFor = (role: AssumedRole) =>
    ROLE_OPTIONS.find((option) => option.key === role)?.label ?? role;

  return (
    <Modal
      open={!!document}
      title={`Role access — ${document?.title ?? ""}`}
      okText="Save access"
      okButtonProps={{ disabled: selected.length === 0 }}
      width={520}
      onCancel={onCancel}
      onOk={() => onSave(selected)}
      destroyOnHidden
    >
      <p className="mb-3 text-[12px] leading-relaxed text-[#6B7280]">
        Access is enforced where retrieval happens, so a role removed here loses
        the document everywhere — including in answers that would have drawn on
        it.
      </p>

      <Checkbox.Group
        className="flex flex-col gap-2"
        value={selected}
        onChange={(values) => setSelected(values as AssumedRole[])}
        options={ROLE_OPTIONS.map((option) => ({
          label: option.label,
          value: option.key,
        }))}
      />

      {selected.length === 0 ? (
        <Alert
          className="mt-4"
          type="error"
          showIcon
          title="At least one role must keep access"
          description="A document no role can open is unreachable but still stored, which is worse than retiring it."
        />
      ) : null}

      {added.length > 0 ? (
        <Alert
          className="mt-4"
          type="warning"
          showIcon
          title={`Widening access to ${added.map(labelFor).join(", ")}`}
          description="They will be able to open every version of this document, and it will inform their answers. This cannot be undone for anything they read."
        />
      ) : null}

      {removed.length > 0 ? (
        <Alert
          className="mt-4"
          type="info"
          showIcon
          title={`Removing access for ${removed.map(labelFor).join(", ")}`}
          description="The change is recorded, but what they have already read stays read."
        />
      ) : null}

      {selected.length >= ALL_ROLES.length ? (
        <p className="mt-3 text-[12px] text-[#6B7280]">
          Every role on the study will have access.
        </p>
      ) : null}
    </Modal>
  );
}
